import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminProfitMargins() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const { data } = await api.get('/bookings');
      setBookings(data);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const bookingsWithB2B = bookings.filter(b => b.b2bCost !== undefined);
  const totalRevenue = bookingsWithB2B.reduce((sum, b) => sum + (b.pricing?.totalCost || 0), 0);
  const totalB2B = bookingsWithB2B.reduce((sum, b) => sum + (b.b2bCost || 0), 0);
  const totalProfit = bookingsWithB2B.reduce((sum, b) => sum + (b.profitMargin || 0), 0);

  // Group by trek
  const byTrek = bookingsWithB2B.reduce((acc, b) => {
    const trek = b.trekName || 'Unknown';
    if (!acc[trek]) acc[trek] = { revenue: 0, b2bCost: 0, profit: 0, count: 0 };
    acc[trek].revenue += b.pricing?.totalCost || 0;
    acc[trek].b2bCost += b.b2bCost || 0;
    acc[trek].profit += b.profitMargin || 0;
    acc[trek].count += 1;
    return acc;
  }, {});
  const trekRows = Object.entries(byTrek).sort((a, b) => b[1].profit - a[1].profit);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Loading...</p></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">📈 Profit Margins</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
          <p className="text-sm text-gray-500">Total Revenue (B2B bookings)</p>
          <p className="text-2xl font-bold text-gray-900">₹{totalRevenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-xl p-5">
          <p className="text-sm text-gray-500">Total B2B Cost</p>
          <p className="text-2xl font-bold text-gray-900">₹{totalB2B.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5">
          <p className="text-sm text-gray-500">Total Profit Margin</p>
          <p className="text-2xl font-bold text-emerald-700">₹{totalProfit.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Profit by Trek */}
      {trekRows.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-8">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h2 className="text-base font-semibold text-gray-800">🏔️ Profit Margins by Trek</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Trek</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Bookings</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Revenue</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">B2B Cost</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Profit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Margin %</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {trekRows.map(([trek, d]) => {
                const marginPct = d.revenue > 0 ? ((d.profit / d.revenue) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={trek} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{trek}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{d.count}</td>
                    <td className="px-4 py-3 text-right">₹{d.revenue.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right text-red-600">₹{d.b2bCost.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-semibold">₹{d.profit.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        parseFloat(marginPct) >= 30 ? 'bg-emerald-100 text-emerald-700' :
                        parseFloat(marginPct) >= 15 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-600'
                      }`}>{marginPct}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bookings Table */}
      {bookingsWithB2B.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <p className="text-gray-400">No bookings have B2B cost assigned yet. Add B2B cost from the Confirmed Bookings page.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Booking ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Customer</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Trek</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Total Cost</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">B2B Cost</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Profit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Margin %</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {bookingsWithB2B.map(b => {
                const totalCost = b.pricing?.totalCost || 0;
                const profit = b.profitMargin || 0;
                const marginPct = totalCost > 0 ? ((profit / totalCost) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{b.id}</td>
                    <td className="px-4 py-3">{b.customerName}</td>
                    <td className="px-4 py-3">{b.trekName}</td>
                    <td className="px-4 py-3 text-right">₹{totalCost.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right text-red-600">₹{(b.b2bCost || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-medium">₹{profit.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right">{marginPct}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t font-semibold">
              <tr>
                <td className="px-4 py-3" colSpan="3">Totals</td>
                <td className="px-4 py-3 text-right">₹{totalRevenue.toLocaleString('en-IN')}</td>
                <td className="px-4 py-3 text-right text-red-600">₹{totalB2B.toLocaleString('en-IN')}</td>
                <td className="px-4 py-3 text-right text-emerald-700">₹{totalProfit.toLocaleString('en-IN')}</td>
                <td className="px-4 py-3 text-right">{totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : '0.0'}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
